import React, { useCallback, useEffect, useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';
import { unchangedSecretForApi } from './adminUtils';

import AdminToggle from './AdminToggle';
import { AdminPanelAlert, AdminPanelHero, AdminPanelSection } from './AdminPanelUi';
import PasswordInput from '../components/PasswordInput';

export default function AdminSiteMailFields({ sessionToken, setStatus }) {
  const [form, setForm] = useState({
    smtp_enabled: false,
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_encryption: 'tls',
    smtp_username: '',
    smtp_password: '',
    has_smtp_password: false,
    smtp_password_needs_reentry: false,
    from_email: 'noreply@salonidesigns.com',
    from_name: 'Saloni Designs',
    reply_to: 'salonidesigns.floral@gmail.com',
    contact_to: 'salonidesigns.floral@gmail.com',
    site_base_url: 'https://salonidesigns.com',
    shipped_emails_enabled: true,
    config_file_exists: false,
    smtp_ready: false,
    php_openssl: true,
  });
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await fetch('/api/admin_mail_settings.php', { headers: adminAuthHeaders(sessionToken) });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setLoadError(d.error || 'Could not load email settings.');
        return;
      }
      const s = d.settings || {};
      setForm({
        smtp_enabled: Boolean(s.smtp_enabled),
        smtp_host: String(s.smtp_host || 'smtp.gmail.com'),
        smtp_port: String(s.smtp_port ?? 587),
        smtp_encryption: String(s.smtp_encryption || 'tls'),
        smtp_username: String(s.smtp_username || ''),
        smtp_password: '',
        has_smtp_password: Boolean(s.has_smtp_password),
        smtp_password_needs_reentry: Boolean(s.smtp_password_needs_reentry),
        from_email: String(s.from_email || ''),
        from_name: String(s.from_name || 'Saloni Designs'),
        reply_to: String(s.reply_to || ''),
        contact_to: String(s.contact_to || ''),
        site_base_url: String(s.site_base_url || 'https://salonidesigns.com'),
        shipped_emails_enabled: s.shipped_emails_enabled !== false,
        config_file_exists: Boolean(s.config_file_exists),
        smtp_ready: Boolean(s.smtp_ready),
        php_openssl: s.php_openssl !== false,
      });
    } catch (e) {
      setLoadError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const revealSavedSmtpPassword = useCallback(async () => {
    if (!form.has_smtp_password || String(form.smtp_password || '').trim()) {
      return Boolean(String(form.smtp_password || '').trim());
    }
    try {
      const r = await fetch('/api/admin_mail_settings.php?include_secrets=1', {
        headers: adminAuthHeaders(sessionToken),
      });
      const d = await r.json();
      if (!r.ok || !d.success) return false;
      const pw = String(d.settings?.smtp_password || '').trim();
      if (!pw) return false;
      setForm((f) => ({ ...f, smtp_password: pw }));
      return true;
    } catch {
      return false;
    }
  }, [sessionToken, form.has_smtp_password, form.smtp_password]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus('');
    try {
      const smtpPassword = unchangedSecretForApi(form.smtp_password, form.has_smtp_password);
      const r = await fetch('/api/admin_mail_settings.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({
          session_token: sessionToken,
          smtp_enabled: form.smtp_enabled,
          smtp_host: form.smtp_host.trim(),
          smtp_port: Math.max(1, parseInt(form.smtp_port, 10) || 587),
          smtp_encryption: form.smtp_encryption,
          smtp_username: form.smtp_username.trim(),
          ...(smtpPassword !== undefined ? { smtp_password: smtpPassword } : {}),
          from_email: form.from_email.trim(),
          from_name: form.from_name.trim(),
          reply_to: form.reply_to.trim(),
          contact_to: form.contact_to.trim(),
          site_base_url: form.site_base_url.trim(),
          shipped_emails_enabled: form.shipped_emails_enabled,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setStatus(d.error || 'Could not save email settings.');
        return;
      }
      const s = d.settings || {};
      setForm((f) => ({
        ...f,
        smtp_enabled: Boolean(s.smtp_enabled),
        smtp_password: '',
        has_smtp_password: Boolean(s.has_smtp_password),
        smtp_password_needs_reentry: Boolean(s.smtp_password_needs_reentry),
        smtp_ready: Boolean(s.smtp_ready),
        config_file_exists: Boolean(s.config_file_exists),
      }));
      setStatus('Email settings saved.');
    } catch (err) {
      setStatus(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setStatus('');
    try {
      const smtpPassword = unchangedSecretForApi(form.smtp_password, form.has_smtp_password);
      const r = await fetch('/api/admin_mail_settings.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({
          session_token: sessionToken,
          action: 'test',
          test_email: testEmail.trim(),
          smtp_enabled: form.smtp_enabled,
          smtp_host: form.smtp_host.trim(),
          smtp_port: form.smtp_port,
          smtp_encryption: form.smtp_encryption,
          smtp_username: form.smtp_username.trim(),
          ...(smtpPassword !== undefined ? { smtp_password: smtpPassword } : {}),
          from_email: form.from_email.trim(),
          from_name: form.from_name.trim(),
          reply_to: form.reply_to.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setStatus(d.error || 'Test email failed.');
        return;
      }
      const hintText = Array.isArray(d.hints) && d.hints.length ? ` ${d.hints.join(' ')}` : '';
      setStatus((d.message || 'Test email sent.') + hintText);
    } catch (err) {
      setStatus(err.message || 'Test failed.');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <p className="small text-muted">Loading email settings…</p>;
  }

  return (
    <form onSubmit={save} className="admin-mail-settings admin-panel-workspace-root">
      <AdminPanelHero
        title="Email"
        lead="Order confirmations, shipping updates, and contact form delivery."
        badge={!form.config_file_exists ? 'Not saved yet' : null}
        badgeWarn={!form.config_file_exists}
      />

      {loadError ? <AdminPanelAlert variant="danger">{loadError}</AdminPanelAlert> : null}
      {!form.config_file_exists ? (
        <AdminPanelAlert variant="warning">Save your settings once to activate email on your live store.</AdminPanelAlert>
      ) : null}

      <AdminPanelSection
        className={!form.smtp_enabled ? ' admin-mail-settings__smtp-card--off' : ''}
        title="SMTP"
        subtitle="Recommended — use Gmail App Password or your host SMTP."
        action={
          <AdminToggle
            id="mail-smtp-enabled"
            label="Send via SMTP"
            checked={form.smtp_enabled}
            onChange={(v) => setForm((f) => ({ ...f, smtp_enabled: v }))}
          />
        }
      >
          {!form.smtp_enabled ? (
            <p className="admin-mail-settings__smtp-off-banner small mb-3">
              SMTP is off — delivery may be unreliable on shared hosting.
            </p>
          ) : null}
          {!form.php_openssl ? (
            <AdminPanelAlert variant="warning">
              Secure mail (Gmail TLS) needs OpenSSL on your host. Try <strong>Preset: GoDaddy relay</strong> if Gmail
              does not work.
            </AdminPanelAlert>
          ) : null}
          <div className="d-flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  smtp_enabled: true,
                  smtp_host: 'smtp.gmail.com',
                  smtp_port: '587',
                  smtp_encryption: 'tls',
                  from_email: f.smtp_username || f.from_email || 'salonidesigns.floral@gmail.com',
                }))
              }
            >
              Preset: Gmail
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  smtp_enabled: true,
                  smtp_host: 'smtp.gmail.com',
                  smtp_port: '465',
                  smtp_encryption: 'ssl',
                  from_email: f.smtp_username || f.from_email || 'salonidesigns.floral@gmail.com',
                }))
              }
            >
              Preset: Gmail SSL 465
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  smtp_enabled: true,
                  smtp_host: 'relay-hosting.secureserver.net',
                  smtp_port: '25',
                  smtp_encryption: 'none',
                  smtp_username: '',
                  smtp_password: '',
                  from_email: f.from_email || 'salonidesigns.floral@gmail.com',
                }))
              }
            >
              Preset: GoDaddy relay
            </button>
          </div>
          <p className="small text-muted mb-0">
            If customers cannot receive password-reset email, reset them under{' '}
            <a href="/account#admin-customers">Account → Customers</a> (expand a customer → Set new password).
          </p>
          <fieldset
            className={`admin-mail-settings__smtp-fieldset${!form.smtp_enabled ? ' admin-mail-settings__smtp-fieldset--locked' : ''}`}
          >
            <legend className="visually-hidden">SMTP server and credentials</legend>
            <div className="admin-mail-settings__smtp-grid">
              <div className="admin-mail-settings__smtp-field admin-mail-settings__smtp-field--host">
                <label className="form-label" htmlFor="mail-smtp-host">
                  SMTP host
                </label>
                <input
                  id="mail-smtp-host"
                  className="form-control"
                  value={form.smtp_host}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))}
                  disabled={!form.smtp_enabled}
                />
              </div>
              <div className="admin-mail-settings__smtp-field">
                <label className="form-label" htmlFor="mail-smtp-port">
                  Port
                </label>
                <input
                  id="mail-smtp-port"
                  type="number"
                  min={1}
                  className="form-control"
                  value={form.smtp_port}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_port: e.target.value }))}
                  disabled={!form.smtp_enabled}
                />
              </div>
              <div className="admin-mail-settings__smtp-field">
                <label className="form-label" htmlFor="mail-smtp-enc">
                  Encryption
                </label>
                <select
                  id="mail-smtp-enc"
                  className="form-select"
                  value={form.smtp_encryption}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_encryption: e.target.value }))}
                  disabled={!form.smtp_enabled}
                >
                  <option value="tls">TLS (587)</option>
                  <option value="ssl">SSL (465)</option>
                  <option value="none">None</option>
                </select>
              </div>
            </div>
            <div className="admin-mail-settings__cred-grid">
              <div className="admin-mail-settings__smtp-field">
                <label className="form-label" htmlFor="mail-smtp-user">
                  Username <span className="text-muted fw-normal">(leave empty for GoDaddy relay)</span>
                </label>
                <input
                  id="mail-smtp-user"
                  className="form-control"
                  value={form.smtp_username}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_username: e.target.value }))}
                  autoComplete="off"
                  disabled={!form.smtp_enabled}
                />
              </div>
              <div className="admin-mail-settings__smtp-field">
                <label className="form-label d-flex flex-wrap align-items-center gap-2" htmlFor="mail-smtp-pass">
                  <span>Password</span>
                  {form.smtp_password_needs_reentry ? (
                    <span className="badge rounded-pill text-bg-warning">Re-enter required</span>
                  ) : form.has_smtp_password ? (
                    <span className="badge rounded-pill text-bg-success">Saved on server</span>
                  ) : (
                    <span className="badge rounded-pill text-bg-secondary">Not saved yet</span>
                  )}
                </label>
                <PasswordInput
                  id="mail-smtp-pass"
                  className="form-control"
                  value={form.smtp_password}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_password: e.target.value }))}
                  placeholder={
                    form.smtp_password_needs_reentry
                      ? 'Enter your Gmail app password again'
                      : form.has_smtp_password
                        ? 'Enter new app password to change'
                        : 'App password'
                  }
                  autoComplete="new-password"
                  disabled={!form.smtp_enabled}
                  onRevealRequest={form.has_smtp_password ? revealSavedSmtpPassword : undefined}
                />
                <p className="form-text mb-0 mt-1">
                  {form.smtp_password_needs_reentry
                    ? 'The stored password was invalid. Enter your real 16-character Gmail App Password, use the eye icon to verify, then Save.'
                    : form.has_smtp_password
                      ? 'Click the eye icon to show the saved password or what you type. Leave blank when saving other settings to keep the current password.'
                      : 'Type your app password and click the eye icon to verify before saving.'}
                </p>
              </div>
            </div>
          </fieldset>
      </AdminPanelSection>

      <AdminPanelSection title="Sender & inbox" subtitle="Addresses customers see and where contact form mail goes.">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="mail-from-email">
                From email
              </label>
              <input
                id="mail-from-email"
                type="email"
                className="form-control"
                value={form.from_email}
                onChange={(e) => setForm((f) => ({ ...f, from_email: e.target.value }))}
              />
              <p className="form-text mb-0">
                With Gmail SMTP, use the <strong>same</strong> address as SMTP username (e.g. your @gmail.com).
              </p>
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="mail-from-name">
                From name
              </label>
              <input
                id="mail-from-name"
                className="form-control"
                value={form.from_name}
                onChange={(e) => setForm((f) => ({ ...f, from_name: e.target.value }))}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="mail-reply-to">
                Reply-To (customer emails)
              </label>
              <input
                id="mail-reply-to"
                type="email"
                className="form-control"
                value={form.reply_to}
                onChange={(e) => setForm((f) => ({ ...f, reply_to: e.target.value }))}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="mail-contact-to">
                Contact form inbox
              </label>
              <input
                id="mail-contact-to"
                type="email"
                className="form-control"
                value={form.contact_to}
                onChange={(e) => setForm((f) => ({ ...f, contact_to: e.target.value }))}
              />
            </div>
            <div className="col-12">
              <label className="form-label" htmlFor="mail-site-base">
                Site URL (links in emails)
              </label>
              <input
                id="mail-site-base"
                type="url"
                className="form-control"
                value={form.site_base_url}
                onChange={(e) => setForm((f) => ({ ...f, site_base_url: e.target.value }))}
                placeholder="https://salonidesigns.com"
              />
            </div>
          </div>
      </AdminPanelSection>

      <AdminPanelSection
        title="Order notifications"
        subtitle="Automatic email when you mark an order as shipped (with tracking if added)."
      >
          <AdminToggle
            id="mail-shipped-enabled"
            className="mb-0"
            labelClassName=""
            label="Send shipped email to customer"
            checked={form.shipped_emails_enabled}
            onChange={(v) => setForm((f) => ({ ...f, shipped_emails_enabled: v }))}
          />
      </AdminPanelSection>

      <AdminPanelSection title="Test email" subtitle="Confirm settings before customers rely on them.">
          <div className="d-flex flex-wrap gap-2 align-items-end">
            <div className="flex-grow-1" style={{ minWidth: 220, maxWidth: 360 }}>
              <label className="form-label small" htmlFor="mail-test-to">
                Send to
              </label>
              <input
                id="mail-test-to"
                type="email"
                className="form-control"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={sendTest}
              disabled={testing || !form.smtp_ready}
            >
              {testing ? 'Sending…' : 'Send test'}
            </button>
          </div>
          {!form.smtp_ready ? (
            <p className="form-text mb-0 mt-2">Enter SMTP host, username, app password, and from email to test.</p>
          ) : (
            <p className="form-text mb-0 mt-2">
              Uses the values above (save to keep them). If the test succeeds but nothing arrives, check Spam /
              Promotions and match From email to your Gmail address.
            </p>
          )}
      </AdminPanelSection>

      <div className="admin-panel-savebar">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save email settings'}
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={load} disabled={saving}>
          Reload
        </button>
      </div>
    </form>
  );
}
